import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { ErrorNotice, Notice, Spinner } from '../components/ui';
import type {
  MemberImportColumns,
  MemberImportPreview,
  MemberImportRow,
} from '../types';

/**
 * Bringing the club's existing membership list in.
 *
 * Three steps, each one a single decision, because this is the screen that
 * decides whether the association ever adopts any of the rest. If moving
 * twenty years of spreadsheet in needs Steve, then this is Steve's system and
 * not theirs.
 *
 *   1. choose the file
 *   2. agree what its columns are      — nothing written
 *   3. look at what would happen       — nothing written
 *   4. press the button                — now it is written
 *
 * The second step exists because a membership list has no standard shape. A
 * bank statement has perhaps five layouts and all of them have a date and an
 * amount; a club list is one volunteer's habits inherited by the next. So the
 * server guesses and a human confirms, rather than the server insisting.
 *
 * What this does NOT bring in is money — no subscriptions, no payments, no
 * "paid 2026 ✓". See MemberImporter for why, and the note on screen says the
 * same thing in fewer words.
 */
export function MemberImportPage() {
  const navigate = useNavigate();

  const [csv, setCsv] = useState<string | null>(null);
  const [fileName, setFileName] = useState('');
  const [columns, setColumns] = useState<MemberImportColumns | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<MemberImportPreview | null>(null);
  const [ticked, setTicked] = useState<Record<number, boolean>>({});
  const [done, setDone] = useState<{ created: number; names: string[] } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function chooseFile(file: File) {
    setError(null);
    setBusy(true);
    setFileName(file.name);
    try {
      const text = await file.text();
      const result = await api.post<MemberImportColumns>('/members/import/columns', { csv: text });
      setCsv(text);
      setColumns(result);
      setMapping(result.mapping);
      setPreview(null);
    } catch (failure) {
      setError(failure);
      setCsv(null);
    } finally {
      setBusy(false);
    }
  }

  async function check() {
    if (csv === null) return;
    setError(null);
    setBusy(true);
    try {
      const result = await api.post<MemberImportPreview>('/members/import/preview', {
        csv,
        mapping,
      });
      setPreview(result);
      // Everything that can be imported starts ticked, because the normal
      // case is importing the lot. Blocked rows cannot be ticked at all.
      setTicked(
        Object.fromEntries(result.rows.map((row) => [row.line, row.state !== 'blocked'])),
      );
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  async function importThem() {
    if (preview === null) return;
    setError(null);
    setBusy(true);
    try {
      const members = preview.rows
        .filter((row) => row.state !== 'blocked' && ticked[row.line])
        .map((row) => row.member);

      const result = await api.post<{ created: number; names: string[] }>('/members/import', {
        members,
      });
      setDone(result);
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  const chosen = preview?.rows.filter((r) => r.state !== 'blocked' && ticked[r.line]).length ?? 0;

  if (done !== null) {
    return (
      <>
        <div className="page-head">
          <h1>Imported</h1>
        </div>
        <Notice kind="success">
          {done.created} {done.created === 1 ? 'member was' : 'members were'} added.
        </Notice>
        <div className="card">
          <h2>What to do next</h2>
          <p>
            Everybody who came in is marked <strong>prospective</strong>, because nothing has been
            paid as far as this system knows. Nobody has been charged anything and no subscription
            has been created.
          </p>
          <p>
            To put them on this year’s books, run the renewal for the current membership year. That
            prices everybody at the rates you have actually set, records what each one owes, and
            leaves the trail an audit needs — which is what makes their subscriptions real rather
            than something this import made up.
          </p>
          <div className="actions">
            <Link to="/members" className="button-link primary">
              See the members
            </Link>
            <Link to="/rates" className="button-link">
              Go to membership years
            </Link>
            <button
              type="button"
              onClick={() => {
                setDone(null);
                setCsv(null);
                setColumns(null);
                setPreview(null);
                setFileName('');
              }}
            >
              Import another file
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <p className="muted small">
        <Link to="/members">← Back to members</Link>
      </p>

      <div className="page-head">
        <h1>Import the membership list</h1>
      </div>

      <ErrorNotice error={error} />

      <ol className="steps">
        {/* ---- 1 ---- */}
        <li className={csv === null ? 'on' : 'done'}>
          <h2>Choose the spreadsheet</h2>
          <p className="hint">
            Save it from Excel or Google Sheets as <strong>CSV</strong> first — in Excel that is
            File, Save As, and choosing “CSV (comma delimited)”. If the workbook has several sheets,
            save just the one with the members on it.
          </p>
          <div className="field">
            <label htmlFor="member-file">Membership list</label>
            <input
              id="member-file"
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void chooseFile(file);
              }}
            />
            {fileName !== '' ? <span className="hint">Reading {fileName}</span> : null}
          </div>
        </li>

        {/* ---- 2 ---- */}
        {columns !== null ? (
          <li className={preview === null ? 'on' : 'done'}>
            <h2>Check the columns</h2>
            <p className="hint">
              {columns.confident.length} of {columns.headers.length} columns were recognised. Change
              anything that is wrong — a column set to “Do not import” is simply left behind.
            </p>
            {columns.truncated ? (
              <Notice kind="warning">
                Only the first {columns.row_count} rows will be read. Split the file and import the
                rest afterwards.
              </Notice>
            ) : null}

            <div className="scroll-x">
              <table className="column-map">
                <thead>
                  <tr>
                    <th scope="col">Column in your file</th>
                    <th scope="col">What it is</th>
                    <th scope="col">First few values</th>
                  </tr>
                </thead>
                <tbody>
                  {columns.headers.map((header, index) => (
                    <tr key={index}>
                      <th scope="row">{header === '' ? <em>(no heading)</em> : header}</th>
                      <td>
                        <select
                          aria-label={`What is the column "${header}"?`}
                          value={mapping[index] ?? 'ignore'}
                          onChange={(e) =>
                            setMapping({ ...mapping, [index]: e.target.value })
                          }
                        >
                          {Object.entries(columns.fields).map(([field, label]) => (
                            <option key={field} value={field}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="muted small">
                        {columns.sample
                          .map((row) => row[index] ?? '')
                          .filter((v) => v !== '')
                          .slice(0, 3)
                          .join(' · ') || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="actions">
              <button type="button" className="primary" disabled={busy} onClick={() => void check()}>
                {busy ? 'Checking…' : `Check all ${columns.row_count} rows`}
              </button>
            </div>
          </li>
        ) : null}

        {/* ---- 3 ---- */}
        {preview !== null ? (
          <li className="on">
            <h2>Look at what would happen</h2>
            <p className="hint">
              {preview.summary.ready} ready, {preview.summary.warning} worth a glance,{' '}
              {preview.summary.blocked} that cannot be imported as they stand. Nothing has been
              written yet.
            </p>

            <Notice kind="info">
              This brings in people, not money. Nobody is charged and no subscription is created —
              once they are in, run the renewal for the current year to put them on the books at the
              rates you have actually set.
            </Notice>

            <div className="scroll-x">
              <table className="import-rows">
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="visually-hidden">Import this one</span>
                    </th>
                    <th scope="col">Row</th>
                    <th scope="col">Who</th>
                    <th scope="col">Where</th>
                    <th scope="col">Contact</th>
                    <th scope="col">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <ImportRow
                      key={row.line}
                      row={row}
                      ticked={ticked[row.line] ?? false}
                      onTick={(on) => setTicked({ ...ticked, [row.line]: on })}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="actions">
              <button
                type="button"
                className="primary"
                disabled={busy || chosen === 0}
                onClick={() => void importThem()}
              >
                {busy ? 'Importing…' : `Import ${chosen} ${chosen === 1 ? 'member' : 'members'}`}
              </button>
              <button type="button" onClick={() => navigate('/members')}>
                Cancel
              </button>
            </div>
          </li>
        ) : null}
      </ol>

      {busy && preview === null && columns === null ? <Spinner label="Reading the file" /> : null}
    </>
  );
}

function ImportRow({
  row,
  ticked,
  onTick,
}: {
  row: MemberImportRow;
  ticked: boolean;
  onTick: (on: boolean) => void;
}) {
  const m = row.member;
  const blocked = row.state === 'blocked';
  const name = [m.title, m.first_name, m.last_name].filter((p) => p !== '').join(' ');

  return (
    <tr className={`import-row is-${row.state}`}>
      <td>
        <input
          type="checkbox"
          className="tick"
          checked={ticked && !blocked}
          disabled={blocked}
          aria-label={`Import ${name === '' ? `row ${row.line}` : name}`}
          onChange={(e) => onTick(e.target.checked)}
        />
      </td>
      <td className="muted small">{row.line}</td>
      <td>
        <strong>{name === '' ? <em>no name</em> : name}</strong>
        {m.status !== '' ? <span className="block small muted">{m.status}</span> : null}
        {m.bbka_number !== '' ? (
          <span className="block small muted">BBKA {m.bbka_number}</span>
        ) : null}
      </td>
      <td className="small">
        {[m.address_1, m.address_2, m.town].filter((p) => p !== '').join(', ')}
        {m.postcode !== '' ? <span className="block">{m.postcode}</span> : null}
      </td>
      <td className="small">
        {m.email !== '' ? <span className="block">{m.email}</span> : null}
        {m.telephone !== '' ? <span className="block muted">{m.telephone}</span> : null}
        {m.mobile !== '' ? <span className="block muted">{m.mobile}</span> : null}
      </td>
      <td className="small">
        {row.notes.length === 0 ? (
          <span className="muted">—</span>
        ) : (
          row.notes.map((note) => (
            <span className="block" key={note}>
              {note}
            </span>
          ))
        )}
      </td>
    </tr>
  );
}
